<script setup lang="ts">
import type { AdminUser, UserCredential, UserRole } from "@maimu/contracts";
import { onMounted, ref } from "vue";

import { api } from "@/lib/api.js";
import { currentUser } from "@/lib/session.js";

/**
 * User management (D19).
 *
 * The password is shown exactly once and never again — not because that is tidy, but
 * because nothing stores it: the row holds a bcrypt hash and the reset endpoint issues
 * a new one rather than recovering the old. The card that shows it says so, or someone
 * closes it expecting to come back later.
 *
 * The columns are chosen to answer the question that actually matters after a
 * rollout: who has opened mAImu at all, and who was only ever handed a login.
 */
const users = ref<AdminUser[]>([]);
const loading = ref(true);
const busy = ref<string | null>(null);
const error = ref<string | null>(null);
const issued = ref<UserCredential | null>(null);
const copied = ref(false);

const draft = ref<{ username: string; role: UserRole }>({ username: "", role: "user" });

function fail(cause: unknown, fallback: string): void {
  error.value = cause instanceof Error ? cause.message : fallback;
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    users.value = await api.adminUsers();
  } catch (cause) {
    fail(cause, "Kasutajaid ei õnnestunud laadida.");
  } finally {
    loading.value = false;
  }
}

async function create(): Promise<void> {
  busy.value = "new";
  error.value = null;
  issued.value = null;
  try {
    issued.value = await api.createAdminUser({ ...draft.value });
    draft.value = { username: "", role: "user" };
    await load();
  } catch (cause) {
    fail(cause, "Kasutaja loomine ebaõnnestus.");
  } finally {
    busy.value = null;
  }
}

async function reset(user: AdminUser): Promise<void> {
  if (!confirm(`Anda kasutajale "${user.username}" uus parool? Vana lakkab kehtimast.`)) return;

  busy.value = user.id;
  error.value = null;
  issued.value = null;
  try {
    issued.value = await api.resetAdminUserPassword(user.id);
  } catch (cause) {
    fail(cause, "Parooli vahetamine ebaõnnestus.");
  } finally {
    busy.value = null;
  }
}

async function toggleRole(user: AdminUser): Promise<void> {
  const role: UserRole = user.role === "admin" ? "user" : "admin";
  busy.value = user.id;
  error.value = null;
  try {
    const updated = await api.setAdminUserRole(user.id, role);
    users.value = users.value.map((row) => (row.id === updated.id ? updated : row));
  } catch (cause) {
    fail(cause, "Õiguste muutmine ebaõnnestus.");
  } finally {
    busy.value = null;
  }
}

async function remove(user: AdminUser): Promise<void> {
  const warning =
    user.conversations > 0
      ? ` Koos temaga kustuvad ka ${user.conversations} vestlust. Vastamata küsimused jäävad alles.`
      : "";
  if (!confirm(`Kustutada kasutaja "${user.username}"?${warning}`)) return;

  busy.value = user.id;
  error.value = null;
  try {
    await api.deleteAdminUser(user.id);
    await load();
  } catch (cause) {
    fail(cause, "Kustutamine ebaõnnestus.");
  } finally {
    busy.value = null;
  }
}

async function copyPassword(): Promise<void> {
  if (!issued.value) return;
  try {
    await navigator.clipboard.writeText(issued.value.password);
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
  } catch {
    // Clipboard is blocked on insecure origins and in some browsers — the password
    // is on screen and selectable, so this is a convenience, not the delivery path.
  }
}

const when = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "—";

onMounted(load);
</script>

<template>
  <section class="users">
    <p class="hint">
      Igaühele oma konto — nii näeb vastamata küsimuste juures, kes küsis, ja väljalangenud töötaja konto saab kustutada
      ilma teisi puutumata. Paroolid genereeritakse, neid ei valita, ja neid näidatakse ainult üks kord.
    </p>

    <div v-if="issued" class="credential">
      <p class="credential-head">
        Konto <strong>{{ issued.user.username }}</strong> — parool on all. <strong>Seda ei näidata uuesti.</strong>
        Anna see edasi turvalist teed pidi ja salvesta paroolihaldurisse.
      </p>
      <div class="credential-row">
        <code>{{ issued.password }}</code>
        <button class="btn-ghost" type="button" @click="copyPassword()">{{ copied ? "Kopeeritud" : "Kopeeri" }}</button>
        <button class="btn-ghost" type="button" @click="issued = null">Sulge</button>
      </div>
    </div>

    <form class="new" @submit.prevent="create()">
      <label class="field">
        <span>Kasutajanimi</span>
        <input v-model.trim="draft.username" type="text" spellcheck="false" placeholder="triin" autocomplete="off" />
      </label>
      <label class="field narrow">
        <span>Roll</span>
        <select v-model="draft.role">
          <option value="user">tavakasutaja</option>
          <option value="admin">administraator</option>
        </select>
      </label>
      <button class="btn" type="submit" :disabled="busy === 'new' || !draft.username">Loo konto</button>
    </form>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="loading" class="hint">Laen…</p>

    <table v-else class="list">
      <thead>
        <tr>
          <th>Kasutaja</th>
          <th>Roll</th>
          <th>Vestlusi</th>
          <th>Ootab vastust</th>
          <th>Viimati aktiivne</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in users" :key="user.id">
          <td>
            <strong>{{ user.username }}</strong>
            <span v-if="user.id === currentUser?.id" class="you">sina</span>
          </td>
          <td>
            <span class="badge" :class="user.role">{{
              user.role === "admin" ? "administraator" : "tavakasutaja"
            }}</span>
          </td>
          <td :class="{ never: !user.conversations }">{{ user.conversations || "ei ole kasutanud" }}</td>
          <td>{{ user.openQuestions || "—" }}</td>
          <td>{{ when(user.lastActiveAt) }}</td>
          <td class="actions">
            <button class="btn-ghost" type="button" :disabled="busy === user.id" @click="reset(user)">
              Uus parool
            </button>
            <button
              class="btn-ghost"
              type="button"
              :disabled="busy === user.id || user.id === currentUser?.id"
              @click="toggleRole(user)"
            >
              {{ user.role === "admin" ? "Võta õigused" : "Tee administraatoriks" }}
            </button>
            <button
              class="btn-ghost danger"
              type="button"
              :disabled="busy === user.id || user.id === currentUser?.id"
              @click="remove(user)"
            >
              Kustuta
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<style scoped>
.users {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.hint {
  color: var(--muted);
  font-size: 0.82rem;
  margin: 0;
  max-width: 700px;
}

.credential {
  border: 1px solid var(--gold);
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  background: var(--soft);
}

.credential-head {
  margin: 0 0 0.5rem;
  font-size: 0.84rem;
}

.credential-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.credential-row code {
  font-size: 1rem;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  user-select: all;
}

.new {
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field.narrow {
  min-width: 150px;
}

.field > span {
  font-size: 0.78rem;
  font-weight: 600;
}

input,
select {
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  font: inherit;
  font-size: 0.86rem;
  background: #fff;
  color: var(--ink);
}

.list {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}

.list th {
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
  border-bottom: 1px solid var(--line);
  padding: 0.4rem 0.5rem;
}

.list td {
  padding: 0.5rem;
  border-bottom: 1px solid var(--line);
  vertical-align: middle;
}

.you {
  margin-left: 0.4rem;
  font-size: 0.7rem;
  color: var(--muted);
}

.never {
  color: var(--muted);
  font-style: italic;
}

.badge {
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
  font-size: 0.72rem;
  font-weight: 600;
}

.badge.admin {
  color: var(--green);
  border-color: var(--green);
}

.actions {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.btn-ghost.danger {
  color: var(--warn);
  border-color: var(--warn);
}

.error {
  margin: 0;
  font-size: 0.82rem;
  border: 1px solid var(--warn);
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  color: var(--warn);
}
</style>
